import type { AgentCategory } from '../../types/platform'

export const DILIGENCE_TO_MONITORING: AgentCategory[] = [
  {
    id: 'diligence',
    label: 'Diligence Agents',
    stage: 'Diligence',
    headline: 'Investigate What Matters.',
    purpose: 'Deploy specialized analysis depending on the decision being made.',
    agents: [
      {
        name: 'Investment Due Diligence Agent',
        purpose: 'Structures investment diligence across team, market, product, traction and risk.',
        inputs: ['Deck', 'Data room', 'Fund criteria'],
        outputs: ['Diligence findings', 'Open questions'],
        useCases: ['Seed to growth rounds', 'Co-investment review'],
        workflows: ['invest'],
      },
      {
        name: 'Financial Due Diligence Agent',
        purpose: 'Reviews revenue, burn, runway, unit economics and financial assumptions from provided information.',
        inputs: ['Financial statements', 'Models'],
        outputs: ['Financial summary', 'Assumption flags'],
        useCases: ['Investment review', 'Supplier financial health'],
        workflows: ['invest', 'acquire', 'assure', 'buy'],
      },
      {
        name: 'Commercial Due Diligence Agent',
        purpose: 'Assesses customers, pricing, go-to-market and commercial traction.',
        inputs: ['Customer data', 'Contracts', 'Pipeline'],
        outputs: ['Commercial assessment'],
        useCases: ['Growth investments', 'Acquisition targets'],
        workflows: ['invest', 'acquire'],
      },
      {
        name: 'Organizational Due Diligence Agent',
        purpose: 'Reviews governance, leadership, structure and operating practices.',
        inputs: ['Org charts', 'Policies', 'Governance documents'],
        outputs: ['Organizational assessment'],
        useCases: ['Partner review', 'Grantee assessment'],
        workflows: ['assure', 'acquire', 'select'],
      },
      {
        name: 'Technology Due Diligence Agent',
        purpose: 'Assesses architecture, product maturity, technical debt and IP considerations.',
        inputs: ['Technical documentation', 'Product materials'],
        outputs: ['Technology assessment'],
        useCases: ['Deep-tech investments', 'Platform acquisitions'],
        workflows: ['invest', 'acquire', 'buy'],
      },
      {
        name: 'Cybersecurity Due Diligence Agent',
        purpose: 'Reviews security policies, questionnaires and controls against your requirements.',
        inputs: ['Security questionnaires', 'Policies', 'Reports'],
        outputs: ['Security findings', 'Control gaps'],
        useCases: ['Vendor security review', 'Target security posture'],
        workflows: ['buy', 'assure', 'acquire'],
      },
      {
        name: 'Data Due Diligence Agent',
        purpose: 'Reviews how an organization collects, stores, governs and uses data.',
        inputs: ['Data policies', 'Data flow documentation'],
        outputs: ['Data handling assessment'],
        useCases: ['AI vendor review', 'Data-driven targets'],
        workflows: ['buy', 'assure', 'acquire'],
      },
      {
        name: 'Procurement Due Diligence Agent',
        purpose: 'Evaluates a proposed purchase against requirements, cost and operational fit.',
        inputs: ['Proposal', 'Requirements', 'Pricing'],
        outputs: ['Procurement findings'],
        useCases: ['Technology procurement', 'Startup pilots'],
        workflows: ['buy'],
      },
      {
        name: 'Vendor Due Diligence Agent',
        purpose: 'Assesses a vendor’s capability, stability and risk before onboarding.',
        inputs: ['Vendor profile', 'References', 'Documents'],
        outputs: ['Vendor assessment'],
        useCases: ['Vendor onboarding', 'Supplier evaluation'],
        workflows: ['buy', 'assure'],
      },
      {
        name: 'Competitive Due Diligence Agent',
        purpose: 'Maps competitors and tests claimed differentiation.',
        inputs: ['Company positioning', 'Market data'],
        outputs: ['Competitive landscape', 'Differentiation review'],
        useCases: ['Investment review', 'Strategic acquisitions'],
        workflows: ['invest', 'acquire'],
      },
      {
        name: 'Market Due Diligence Agent',
        purpose: 'Tests market size, growth assumptions and timing.',
        inputs: ['Market claims', 'Research'],
        outputs: ['Market assessment', 'Assumption review'],
        useCases: ['New category bets', 'Market entry'],
        workflows: ['invest', 'acquire'],
      },
      {
        name: 'Custom Diligence Agent',
        purpose: 'A diligence agent configured around your own framework, questions and evidence rules.',
        inputs: ['Your framework', 'Your documents'],
        outputs: ['Custom assessment'],
        useCases: ['ESG review', 'Impact assessment'],
        workflows: ['invest', 'buy', 'acquire', 'select', 'assure', 'monitor'],
      },
    ],
  },
  {
    id: 'workflow',
    label: 'Workflow Agents',
    stage: 'Workflow',
    headline: 'Move Work Through Your Organization.',
    purpose: 'Coordinate stages, reviewers, approvals and exceptions.',
    agents: [
      {
        name: 'Workflow Builder',
        purpose: 'Defines the stages, agents, requirements and reviewers in a process.',
        inputs: ['Your current process'],
        outputs: ['Configured workflow'],
        useCases: ['New program setup', 'Process redesign'],
        workflows: ['invest', 'buy', 'acquire', 'select', 'assure', 'monitor'],
      },
      {
        name: 'Routing Agent',
        purpose: 'Sends each record to the right stage, team or reviewer based on your rules.',
        inputs: ['Record', 'Routing rules'],
        outputs: ['Assignment'],
        useCases: ['Sector-based routing', 'Regional teams'],
        workflows: ['invest', 'buy', 'select'],
      },
      {
        name: 'Approval Agent',
        purpose: 'Collects the approvals a stage requires and records who approved what.',
        inputs: ['Approval policy', 'Findings'],
        outputs: ['Approval record'],
        useCases: ['Procurement sign-off', 'Investment approvals'],
        workflows: ['buy', 'invest', 'acquire'],
      },
      {
        name: 'Review Agent',
        purpose: 'Prepares review packets and collects reviewer scores and comments.',
        inputs: ['Assessment', 'Reviewer list'],
        outputs: ['Review packet', 'Reviewer feedback'],
        useCases: ['Panel review', 'Partner review'],
        workflows: ['select', 'invest'],
      },
      {
        name: 'Committee Agent',
        purpose: 'Assembles committee materials, agendas and recorded outcomes.',
        inputs: ['Memos', 'Votes', 'Agenda'],
        outputs: ['Committee pack', 'Decision record'],
        useCases: ['Investment committee', 'Grant committee'],
        workflows: ['invest', 'select', 'acquire'],
      },
      {
        name: 'Escalation Agent',
        purpose: 'Raises exceptions and blocked items to the right owner.',
        inputs: ['Exception rules', 'Status'],
        outputs: ['Escalation alert'],
        useCases: ['Critical risk flags', 'Stalled reviews'],
        workflows: ['buy', 'assure', 'monitor'],
      },
    ],
  },
  {
    id: 'documents',
    label: 'Document Agents',
    stage: 'Document',
    headline: 'Turn Analysis Into Institutional Outputs.',
    purpose: 'Populate standardized documents using structured diligence results.',
    agents: [
      {
        name: 'Investment Memo Agent',
        purpose: 'Drafts an investment memo from structured findings for your team to edit.',
        inputs: ['Findings', 'Memo template'],
        outputs: ['Memo draft'],
        useCases: ['Partner review', 'IC preparation'],
        workflows: ['invest'],
      },
      {
        name: 'IC Template Agent',
        purpose: 'Fills your investment committee template with the current deal record.',
        inputs: ['Deal record', 'IC template'],
        outputs: ['IC document'],
        useCases: ['Weekly IC', 'Follow-on decisions'],
        workflows: ['invest'],
      },
      {
        name: 'Procurement Assessment Agent',
        purpose: 'Produces a standardized procurement assessment for approvers.',
        inputs: ['Review findings', 'Template'],
        outputs: ['Procurement assessment'],
        useCases: ['Technology purchases', 'Pilot approval'],
        workflows: ['buy'],
      },
      {
        name: 'RFP Evaluation Agent',
        purpose: 'Compiles proposal evaluations into a comparable format.',
        inputs: ['Proposals', 'Evaluation criteria'],
        outputs: ['Evaluation report', 'Comparison table'],
        useCases: ['Public tenders', 'Corporate RFPs'],
        workflows: ['select', 'buy'],
      },
      {
        name: 'Grant Assessment Agent',
        purpose: 'Drafts grant assessments aligned to program criteria.',
        inputs: ['Application', 'Program criteria'],
        outputs: ['Assessment draft'],
        useCases: ['Government grants', 'Foundation programs'],
        workflows: ['select'],
      },
      {
        name: 'Risk Report Agent',
        purpose: 'Summarizes identified risks, evidence and mitigations.',
        inputs: ['Risk findings'],
        outputs: ['Risk report'],
        useCases: ['Third-party risk', 'Acquisition risk'],
        workflows: ['assure', 'buy', 'acquire'],
      },
      {
        name: 'Executive Summary Agent',
        purpose: 'Condenses a full assessment into a one-page summary for decision makers.',
        inputs: ['Full assessment'],
        outputs: ['Executive summary'],
        useCases: ['Board updates', 'Leadership briefings'],
        workflows: ['invest', 'buy', 'acquire', 'assure'],
      },
      {
        name: 'Custom Template Agent',
        purpose: 'Populates any document template you provide with structured results.',
        inputs: ['Your template', 'Findings'],
        outputs: ['Completed document'],
        useCases: ['Internal forms', 'Regulatory filings'],
        workflows: ['invest', 'buy', 'acquire', 'select', 'assure', 'monitor'],
      },
    ],
  },
  {
    id: 'monitoring',
    label: 'Monitoring Agents',
    stage: 'Monitor',
    headline: 'Diligence Does Not Stop at the Decision.',
    purpose: 'Continue collecting information after a decision has been made.',
    agents: [
      {
        name: 'Portfolio Monitoring Agent',
        purpose: 'Collects and structures portfolio company updates over time.',
        inputs: ['Company updates', 'KPIs'],
        outputs: ['KPI history', 'Portfolio view'],
        useCases: ['Quarterly reviews', 'Follow-on decisions'],
        workflows: ['invest', 'monitor'],
      },
      {
        name: 'Vendor Monitoring Agent',
        purpose: 'Tracks vendor status, renewals and changes after onboarding.',
        inputs: ['Vendor record', 'Updates'],
        outputs: ['Vendor status', 'Change alerts'],
        useCases: ['Renewal reviews', 'Ongoing third-party risk'],
        workflows: ['buy', 'monitor'],
      },
      {
        name: 'Grant Milestone Agent',
        purpose: 'Tracks grant milestones and reporting obligations.',
        inputs: ['Milestones', 'Grantee reports'],
        outputs: ['Milestone status'],
        useCases: ['Disbursement checks', 'Program reporting'],
        workflows: ['select', 'monitor'],
      },
      {
        name: 'Company Update Agent',
        purpose: 'Requests and structures periodic updates from monitored organizations.',
        inputs: ['Update schedule', 'Template'],
        outputs: ['Structured updates'],
        useCases: ['Founder updates', 'Partner check-ins'],
        workflows: ['invest', 'monitor'],
      },
      {
        name: 'Financial Monitoring Agent',
        purpose: 'Tracks financial metrics and highlights changes against prior periods.',
        inputs: ['Financial reports'],
        outputs: ['Trend summary', 'Variance flags'],
        useCases: ['Runway tracking', 'Supplier health'],
        workflows: ['invest', 'assure', 'monitor'],
      },
      {
        name: 'Risk Change Agent',
        purpose: 'Flags new or changed risk signals for previously approved records.',
        inputs: ['Approved records', 'New information'],
        outputs: ['Risk change alerts'],
        useCases: ['Counterparty risk', 'Vendor incidents'],
        workflows: ['assure', 'buy', 'monitor'],
      },
      {
        name: 'Contract Milestone Agent',
        purpose: 'Tracks contractual milestones, obligations and renewal dates.',
        inputs: ['Contract terms', 'Dates'],
        outputs: ['Milestone calendar', 'Reminders'],
        useCases: ['Pilot conversions', 'Renewals'],
        workflows: ['buy', 'acquire', 'monitor'],
      },
      {
        name: 'Reporting Agent',
        purpose: 'Produces recurring reports from monitored data, such as LP or program reports.',
        inputs: ['Monitored data', 'Report template'],
        outputs: ['Report draft'],
        useCases: ['LP reporting', 'Program impact reports'],
        workflows: ['invest', 'select', 'monitor'],
      },
    ],
  },
]
